// PartyPicker — THE client picker of the app family (APP-FAMILY-CONTRACT
// §3.1, gap G-D). Orders, Invoices and Production pick a client with this one
// component — and add one, with `allowCreate` — in a workspace with or
// without CRM. It IS the shared AppCombobox (the WAI-ARIA combobox: roles,
// ↓ ↑ Enter Esc Tab, the in-flow list, the status line) over the workspace's
// own Parties, plus an in-flow quick add.
//
//   PartyPicker: {
//     kind: 'company',                 // 'company' | 'person' | 'any' (default)
//     partyRole: 'customer',           // a party the quick add creates gets this role
//     current: (el, s) => s.root.ordForm.party,   // the chosen Party id
//     select: 'ordFormPickParty',      // el.call(select, { id, name, kind, taxId })
//     door: 'ordData',                 // optional — el.call(door, 'parties', op, args)
//     allowCreate: true,
//     ariaLabel: (el) => el.call('polyglot', 'ordTxFieldClient')   // optional
//   }
//
// THE NAMES. The contract's `role` and `value` are taken in DOMQL — `role` is
// the box's ARIA role, `value` an input's value — so they are `partyRole` and
// `current` here (`current` is what AppCombobox and AppSegmented already call
// the chosen value). `placeholder` and `addLabel` (the "New client" row and
// the form's title) are bundle keys or plain strings.
//
// DATA. The first open reads the directory ONCE (`parties.list`, staff left
// out with `member: 'exclude'`, `kind` unless 'any'); typing filters it at
// once — name, legal name, email, tax id and registration number (the exact
// id ranks first; spaces, dashes, dots, slashes and case do not count: the
// server's normalizeIdentityValue rule), phone (any separators, with or
// without the country code) — and asks the server too (`q`, 2+ characters,
// debounced, the newest query wins), so a row the server matches and the
// loaded rows lack still shows. The role is NOT a list filter: CRM creates
// contacts without role rows, and a role filter would hide them. Requests go
// through `door` when the app has one (the door pins the workspace), else
// straight to the L1 SDK pinned to `s.root.workspace` — never unpinned: with
// no workspace yet the read fails, says so, and the next open tries again.
// The chosen party shows its name before any open (one `parties.get`).
//
// THE QUICK ADD (`allowCreate`). The last option, "New client" (↑ from the
// first row reaches it), opens a form under the field with the typed text as
// the name: { kind, name, legalName, taxId, email, phone }. The duplicate
// rule — a tax id another party has is REFUSED: nothing is created, that
// party is returned through `select` and the status line names it. Archived
// rows count: the check asks the server for the exact id (`taxId`,
// `includeArchived`); SDK 3.14.822 drops `taxId`, so today the answer is the
// whole directory and the exact match runs here. A similar name WARNS once:
// "Use …" or "Add anyway". Keyboard: Enter in a field adds, Esc closes the
// form (and stops there, so a dialog around it stays), the focus goes to the
// name on open and back to the field — list closed — after.
//
// THE ROW WASHES are the shell's CSS variables, not the token names: an
// installed app renders under its own config, which has no `hover` /
// `selected` (the AppSegmented cause, __tests__/appSegmentedStateTokens.test.js),
// and the keyboard's active row must show there too.
//
// Helpers live in `scope` (frank-safe, any function registry). Per-instance
// timers and sequence numbers sit on the box's own scope object, written as
// `box.scope.X` — a child's `el.scope` only reads through to it.
export const PartyPicker = {
  extends: 'AppCombobox',

  kind: 'any',
  partyRole: '',
  current: null,
  select: '',
  door: '',
  allowCreate: false,
  placeholder: 'common.party.placeholder',
  addLabel: 'common.party.addNew',
  ariaLabel: (el) => el.call('polyglot', 'common.party.label'),
  searchDelay: 250,
  limit: 50,

  // Every key the picker reads is held here: a key a state lacks reads
  // through to the ROOT state.
  state: {
    query: '',
    open: false,
    active: 0,
    parties: [],
    loaded: false,
    loading: false,
    loadError: false,
    hits: [],
    hitsFor: '',
    searching: false,
    pickedId: '',
    pickedName: '',
    pickedKind: '',
    pickedTaxId: '',
    note: '',
    noteName: '',
    adding: false,
    addKind: 'company',
    addName: '',
    addLegalName: '',
    addTaxId: '',
    addEmail: '',
    addPhone: '',
    addBusy: false,
    addError: '',
    addNameError: false,
    addSimilarId: '',
    addSimilarName: ''
  },

  scope: {
    partyAddValue: '__add__',

    // ── the filter (pure) ────────────────────────────────────────────────
    partyText(v) {
      return String(v == null ? '' : v)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()
    },
    // The server's identity key: spaces, dashes, dots and slashes do not
    // count, case does not count; '' when empty or longer than 64.
    partyTaxKey(v) {
      const k = String(v == null ? '' : v)
        .replace(/[\s\-./]+/g, '')
        .toUpperCase()
      return k.length && k.length <= 64 ? k : ''
    },
    // Digits only; a leading 00 is the international prefix.
    partyDigits(v) {
      const d = String(v == null ? '' : v).replace(/\D/g, '')
      return d.indexOf('00') === 0 ? d.slice(2) : d
    },
    // Digits and phone separators only, 6 to 20 digits (the server's rule).
    partyPhoneShaped(v) {
      const t = String(v == null ? '' : v).trim()
      if (!/^\+?[\d\s\-().]+$/.test(t)) return false
      const n = t.replace(/\D/g, '').length
      return n >= 6 && n <= 20
    },
    partyArr(v) {
      return Array.isArray(v) ? v : []
    },
    // The search keys of a row, computed once per row.
    partyKeys(r) {
      return {
        name: this.partyText(r.name),
        legal: this.partyText(r.legalName),
        tax: this.partyTaxKey(r.taxId),
        reg: this.partyTaxKey(r.regNo),
        emails: this.partyArr(r.emails).map((e) => this.partyText(e)),
        phones: this.partyArr(r.phones).map((p) => this.partyDigits(p))
      }
    },
    // A server row (the Party DTO) → the compact row the picker keeps.
    partyRow(p) {
      if (!p || typeof p !== 'object') return null
      const rawId = p.id != null ? p.id : p._id
      if (rawId == null || rawId === '') return null
      const str = (v) => (v == null ? '' : String(v).trim())
      const values = (list, first) => {
        const out = []
        const push = (v) => {
          const t = str(v && typeof v === 'object' ? v.value : v)
          if (t && out.indexOf(t) === -1) out.push(t)
        }
        push(first)
        for (const v of Array.isArray(list) ? list : []) push(v)
        return out
      }
      const id = String(rawId)
      const person = [str(p.firstName), str(p.lastName)].filter(Boolean).join(' ')
      const row = {
        id,
        name: str(p.name) || person || str(p.legalName) || id,
        kind: p.kind === 'person' || p.kind === 'company' ? p.kind : '',
        taxId: str(p.taxId),
        regNo: str(p.regNo),
        legalName: str(p.legalName),
        emails: values(p.emails, p.primaryEmail || p.email),
        phones: values(p.phones, p.phoneE164 || p.phone),
        member: p.user != null && p.user !== '',
        archived: !!p.archivedAt,
        gone: !!p.deleted || (p.mergedInto != null && p.mergedInto !== '')
      }
      row.find = this.partyKeys(row)
      return row
    },
    // A list answer (an array or { data: [] }) → compact rows.
    partyRows(res) {
      const list = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
      const out = []
      for (const p of list) {
        const r = this.partyRow(p)
        if (r) out.push(r)
      }
      return out
    },
    // A one-row answer (the row or { data: row }).
    partyOne(res) {
      return res && typeof res === 'object' && res.data && typeof res.data === 'object'
        ? res.data
        : res
    },
    partyKindOf(box) {
      const k = String((box && box.kind) || '').toLowerCase()
      return k === 'company' || k === 'person' ? k : 'any'
    },
    // Listed: a client — never staff (a member's party), never archived or
    // merged away — of the asked kind.
    partyListable(r, kind) {
      if (!r || r.member || r.archived || r.gone) return false
      return kind === 'company' || kind === 'person' ? r.kind === kind : true
    },
    // A match → its rank (0 is best); -1: no match. The exact tax id (or
    // registration number) first; the name — a prefix, a word start, a
    // contains — with the legal name under each; then a tax-id fragment,
    // an email, a phone.
    partyRank(r, query) {
      const q = this.partyText(query)
      if (!q) return 0
      const f = r.find || this.partyKeys(r)
      const qTax = this.partyTaxKey(query)
      if (qTax && (qTax === f.tax || qTax === f.reg)) return 0
      if (f.name.indexOf(q) === 0) return 1
      if (f.legal && f.legal.indexOf(q) === 0) return 2
      const wordStart = (t) => t.split(/[^\p{L}\p{N}@.]+/u).some((w) => w && w.indexOf(q) === 0)
      if (wordStart(f.name) || (f.legal && wordStart(f.legal))) return 3
      if (f.name.indexOf(q) >= 0 || (f.legal && f.legal.indexOf(q) >= 0)) return 4
      if (
        qTax.length >= 3 &&
        ((f.tax && f.tax.indexOf(qTax) >= 0) || (f.reg && f.reg.indexOf(qTax) >= 0))
      )
        return 5
      if (f.emails.some((e) => e.indexOf(q) >= 0)) return 6
      if (this.partyPhoneShaped(query)) {
        const qd = this.partyDigits(query)
        if (f.phones.some((d) => d.length >= 6 && (d.indexOf(qd) >= 0 || qd.indexOf(d) >= 0)))
          return 7
      }
      return -1
    },
    // The listed rows that match, best first, then by name.
    partyFilter(rows, query, kind) {
      const k = kind === 'company' || kind === 'person' ? kind : 'any'
      const ranked = []
      for (const r of this.partyArr(rows)) {
        if (!this.partyListable(r, k)) continue
        const rank = this.partyRank(r, query)
        if (rank >= 0) ranked.push({ r, rank })
      }
      ranked.sort((a, b) => a.rank - b.rank || a.r.name.localeCompare(b.r.name))
      return ranked.map((x) => x.r)
    },
    // The quick add's duplicate rule → { exact, similar }. `exact`: a party
    // with the same tax id — archived ones count (the add is refused).
    // `similar`: a live party whose name or legal name is the same, or holds
    // the other (4+ letters; shorter names compare whole) — warned once.
    // Staff and merged rows are never the existing client.
    partyDuplicate(draft, rows) {
      const d = draft || {}
      const list = this.partyArr(rows).filter((r) => r && !r.member && !r.gone)
      const tax = this.partyTaxKey(d.taxId)
      const exact = tax ? list.find((r) => this.partyTaxKey(r.taxId) === tax) || null : null
      const name = this.partyText(d.name)
      let similar = null
      if (name) {
        const near = (t) => t.length >= 4 && (t.indexOf(name) >= 0 || name.indexOf(t) >= 0)
        similar =
          list.find((r) => {
            if (r.archived) return false
            const own = this.partyText(r.name)
            const legal = this.partyText(r.legalName)
            if (own === name || (legal && legal === name)) return true
            if (name.length < 4) return false
            return near(own) || (!!legal && near(legal))
          }) || null
      }
      return { exact, similar }
    },
    // What `select` receives: exactly { id, name, kind, taxId }.
    partySummary(r) {
      if (!r) return null
      return { id: r.id, name: r.name, kind: r.kind || null, taxId: r.taxId || null }
    },
    // The row's secondary line: what tells two similar clients apart.
    partyHint(r) {
      const parts = []
      if (r.legalName && this.partyText(r.legalName) !== this.partyText(r.name))
        parts.push(r.legalName)
      if (r.taxId) parts.push(r.taxId)
      const contact = this.partyArr(r.emails)[0] || this.partyArr(r.phones)[0]
      if (contact) parts.push(contact)
      return parts.join(' · ')
    },
    // A bundle string with its {name} filled (a function replacement: a
    // name with `$&` in it stays as typed).
    partyCopy(box, key, name) {
      const text = String(box.call('polyglot', key) || '')
      return text.replace(/\{name\}/g, () => String(name == null ? '' : name))
    },

    // ── the combobox, specialised ──────────────────────────────────────
    partyCurrent(box) {
      const c = box.current
      return c == null ? '' : String(c)
    },
    partyPicked(st) {
      return this.partyRow({
        id: st.pickedId,
        name: st.pickedName,
        kind: st.pickedKind,
        taxId: st.pickedTaxId
      })
    },
    partyFind(box, id) {
      const want = id == null ? '' : String(id)
      if (!want) return null
      const s = box.state
      for (const r of this.partyArr(s.parties)) if (r && r.id === want) return r
      for (const r of this.partyArr(s.hits)) if (r && r.id === want) return r
      return s.pickedId === want ? this.partyPicked(s) : null
    },
    partyMatches(box, s, query) {
      const st = s || {}
      const q = String(query == null ? '' : query).trim()
      const kind = this.partyKindOf(box)
      const pool = this.partyArr(st.parties).slice()
      if (st.pickedId && !pool.some((r) => r && r.id === st.pickedId))
        pool.push(this.partyPicked(st))
      const local = this.partyFilter(pool, q, kind)
      const seen = {}
      for (const r of local) seen[r.id] = true
      // what the server matched for THIS query and the loaded rows lack
      const extra =
        q && st.hitsFor === q
          ? this.partyArr(st.hits).filter((r) => r && !seen[r.id] && this.partyListable(r, kind))
          : []
      const all = local.concat(extra)
      const cap = Number(box.limit) || 50
      const shown = all.slice(0, cap).map((r) => ({
        value: r.id,
        label: r.name,
        hint: this.partyHint(r),
        isAdd: false
      }))
      const more = all.length - shown.length
      let statusKey = ''
      if (!all.length)
        statusKey = st.loadError
          ? 'common.party.loadFailed'
          : st.loading || (q && st.searching)
            ? 'common.party.searching'
            : 'common.state.noMatches'
      else if (more > 0) statusKey = 'common.hint.keepTyping'
      if (box.allowCreate)
        shown.push({
          value: this.partyAddValue,
          label: String(box.call('polyglot', box.addLabel || 'common.party.addNew')),
          hint: q,
          isAdd: true
        })
      return { shown, more, statusKey }
    },
    matches(box, s) {
      return this.partyMatches(box, s, s && s.query)
    },
    label(box) {
      const r = this.partyFind(box, this.partyCurrent(box))
      return r ? r.name : ''
    },
    open(box) {
      const s = box.state
      this.partyLoad(box)
      const shown = this.partyMatches(box, s, '').shown
      const at = shown.findIndex((o) => o.value === this.partyCurrent(box))
      s.update({ open: true, query: '', active: at === -1 ? 0 : at, note: '', noteName: '' })
    },
    pick(box, value) {
      const s = box.state
      if (value === this.partyAddValue) return this.partyAddOpen(box, s.query)
      const r = this.partyFind(box, value)
      s.update({ open: false, query: '' })
      if (r) this.partyChoose(box, r, '')
    },
    // Choose a party: remember it (its name shows before the directory
    // loads) and hand its summary to `select`. `note`: a status line naming it.
    partyChoose(box, r, note) {
      const sum = this.partySummary(r)
      box.state.update({
        pickedId: sum.id,
        pickedName: sum.name,
        pickedKind: sum.kind || '',
        pickedTaxId: sum.taxId || '',
        note: note || '',
        noteName: note ? sum.name : '',
        adding: false
      })
      if (typeof box.select === 'string' && box.select) box.call(box.select, sum)
      return sum
    },
    // The status line: the list's status while open; a note after.
    partyStatus(box, s) {
      if (s.open) {
        const m = this.matches(box, s)
        return m.statusKey
          ? String(box.call('polyglot', m.statusKey)).replace('{count}', String(m.more))
          : ''
      }
      return s.note ? this.partyCopy(box, s.note, s.noteName) : ''
    },

    // ── data ────────────────────────────────────────────────────────────
    // One call. Through the app's door when it has one; else the L1 SDK,
    // pinned to this tab's workspace — never unpinned (the server would
    // answer for the caller's other workspace).
    partyCall(box, op, args) {
      const door = typeof box.door === 'string' ? box.door : ''
      if (door) return box.call(door, 'parties', op, args || {})
      const sdk = box.sdk || (box.context && box.context.sdk)
      if (!sdk || typeof sdk.execute !== 'function') throw new Error('sdk_unavailable')
      const root = box.state && box.state.root
      const ws = root && root.workspace
      const wsId = ws && (ws.id || ws._id)
      if (!wsId) throw new Error('workspace_not_ready')
      return sdk.execute('parties', op, { ...(args || {}), workspaceId: String(wsId) })
    },
    // The directory, once; a failed read is tried again on the next open.
    partyLoad(box) {
      const s = box.state
      const sc = box.scope
      if (s.loaded || s.loading) return sc.partyLoading || Promise.resolve()
      const kind = this.partyKindOf(box)
      const filter = { member: 'exclude' }
      if (kind !== 'any') filter.kind = kind
      s.update({ loading: true, loadError: false })
      const run = Promise.resolve()
        .then(() => this.partyCall(box, 'list', { filter }))
        .then(
          (res) => {
            if (sc.partyGone) return
            s.update({ parties: this.partyRows(res), loaded: true, loading: false })
            // opened before the rows came: the chosen one becomes active
            if (s.open && !String(s.query || '').trim()) {
              const cur = this.partyCurrent(box)
              const at = this.partyMatches(box, s, '').shown.findIndex((o) => o.value === cur)
              if (at > 0) s.update({ active: at })
            }
          },
          () => {
            if (!sc.partyGone) s.update({ loading: false, loadError: true })
          }
        )
      sc.partyLoading = run
      return run
    },
    partySearchSoon(box) {
      const sc = box.scope
      clearTimeout(sc.partyTimer)
      this.partyLoad(box)
      const q = String(box.state.query || '').trim()
      if (q.length < 2) return
      const wait = Number(box.searchDelay)
      sc.partyTimer = setTimeout(() => this.partySearch(box, q), wait >= 0 ? wait : 250)
    },
    // The server's matches for `q`; an answer older than the newest search
    // is dropped.
    partySearch(box, q) {
      const sc = box.scope
      const s = box.state
      if (sc.partyGone) return Promise.resolve()
      const seq = (sc.partySeq || 0) + 1
      sc.partySeq = seq
      const kind = this.partyKindOf(box)
      const filter = { q, member: 'exclude' }
      if (kind !== 'any') filter.kind = kind
      s.update({ searching: true })
      return Promise.resolve()
        .then(() => this.partyCall(box, 'list', { filter }))
        .then(
          (res) => this.partyRows(res),
          () => null
        )
        .then((rows) => {
          if (sc.partyGone || sc.partySeq !== seq) return
          s.update(rows ? { hits: rows, hitsFor: q, searching: false } : { searching: false })
        })
    },
    // The chosen party's name before any open: one get by id.
    partyResolve(box) {
      const sc = box.scope
      const id = this.partyCurrent(box)
      if (!id || sc.partyResolving === id || this.partyFind(box, id)) return
      sc.partyResolving = id
      Promise.resolve()
        .then(() => this.partyCall(box, 'get', { id }))
        .then(
          (res) => this.partyRow(this.partyOne(res)),
          () => null
        )
        .then((r) => {
          if (sc.partyResolving === id) sc.partyResolving = ''
          if (sc.partyGone || !r || this.partyCurrent(box) !== id) return
          box.state.update({
            pickedId: r.id,
            pickedName: r.name,
            pickedKind: r.kind,
            pickedTaxId: r.taxId
          })
        })
    },

    // ── the quick add ───────────────────────────────────────────────────
    partyAddOpen(box, name) {
      const kind = this.partyKindOf(box)
      box.state.update({
        open: false,
        query: '',
        note: '',
        noteName: '',
        adding: true,
        addKind: kind === 'person' ? 'person' : 'company',
        addName: String(name || '').trim(),
        addLegalName: '',
        addTaxId: '',
        addEmail: '',
        addPhone: '',
        addBusy: false,
        addError: '',
        addNameError: false,
        addSimilarId: '',
        addSimilarName: ''
      })
      this.partyFocusField(box, 'name')
    },
    partyAddClose(box) {
      box.state.update({
        adding: false,
        addBusy: false,
        addError: '',
        addNameError: false,
        addSimilarId: '',
        addSimilarName: ''
      })
      this.partyFocusInput(box)
    },
    // One field of the draft; an edit clears the last check's answer.
    partyAddSet(box, key, value) {
      const patch = { addError: '', addSimilarId: '', addSimilarName: '' }
      patch[key] = String(value == null ? '' : value)
      if (key === 'addName') patch.addNameError = false
      box.state.update(patch)
    },
    // Enter in a field adds (a button keeps its own Enter); Esc closes the
    // form and stops there.
    partyAddKey(box, e) {
      if (!e || e.isComposing) return
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        this.partyAddClose(box)
        return
      }
      if (e.key !== 'Enter') return
      const tag = e.target && e.target.tagName ? String(e.target.tagName).toUpperCase() : ''
      if (tag !== 'INPUT') return
      e.preventDefault()
      this.partyAddRun(box, false)
    },
    // Add: a name is required; the same tax id is refused (the existing
    // party is returned); a similar name warns once (`force` = "Add
    // anyway"); then create, give the role, choose it.
    async partyAddRun(box, force) {
      const s = box.state
      const sc = box.scope
      if (s.addBusy) return null
      const name = String(s.addName || '').trim()
      if (!name) {
        s.update({ addNameError: true, addSimilarId: '', addSimilarName: '' })
        this.partyFocusField(box, 'name')
        return null
      }
      const fixed = this.partyKindOf(box)
      const kind = fixed !== 'any' ? fixed : s.addKind === 'person' ? 'person' : 'company'
      const tax = String(s.addTaxId || '').trim()
      const taxKey = this.partyTaxKey(tax)
      s.update({ addBusy: true, addError: '', addNameError: false })
      await this.partyLoad(box)
      if (sc.partyGone) return null
      const rows = this.partyArr(s.parties)
      if (taxKey) {
        let exact = this.partyDuplicate({ taxId: taxKey }, rows).exact
        if (!exact) {
          let answer = null
          try {
            const res = await this.partyCall(box, 'list', {
              filter: { taxId: taxKey, includeArchived: true }
            })
            answer = this.partyRows(res)
          } catch (err) {
            answer = null
          }
          if (sc.partyGone) return null
          // the check could not run: never create blind
          if (!answer) {
            s.update({ addBusy: false, addError: 'common.party.addFailed' })
            return null
          }
          exact = this.partyDuplicate({ taxId: taxKey }, answer).exact
        }
        if (exact) {
          s.update({ addBusy: false, addSimilarId: '', addSimilarName: '' })
          const sum = this.partyChoose(box, exact, 'common.party.idExists')
          this.partyFocusInput(box)
          return sum
        }
      }
      if (force !== true) {
        const similar = this.partyDuplicate({ name }, rows).similar
        if (similar) {
          s.update({ addBusy: false, addSimilarId: similar.id, addSimilarName: similar.name })
          return null
        }
      }
      const payload = { kind, name }
      const legal = String(s.addLegalName || '').trim()
      const email = String(s.addEmail || '').trim()
      const phone = String(s.addPhone || '').trim()
      if (kind === 'company' && legal) payload.legalName = legal
      if (tax) payload.taxId = tax
      if (email) payload.emails = [{ value: email, primary: true }]
      if (phone) payload.phones = [{ value: phone, primary: true }]
      let row = null
      try {
        row = this.partyRow(this.partyOne(await this.partyCall(box, 'create', { payload })))
      } catch (err) {
        row = null
      }
      if (sc.partyGone) return null
      if (!row) {
        s.update({ addBusy: false, addError: 'common.party.addFailed' })
        return null
      }
      const role = String(box.partyRole || '').trim()
      // the party exists either way; its role is best effort
      if (role)
        await Promise.resolve()
          .then(() => this.partyCall(box, 'addRole', { partyId: row.id, role }))
          .catch(() => null)
      if (sc.partyGone) return null
      s.update({
        parties: [row].concat(this.partyArr(s.parties).filter((r) => r && r.id !== row.id)),
        addBusy: false
      })
      const sum = this.partyChoose(box, row, '')
      this.partyFocusInput(box)
      return sum
    },
    // "Use …" on the similar-name warning.
    partyUseSimilar(box) {
      const s = box.state
      const r = this.partyFind(box, s.addSimilarId)
      s.update({ adding: false, addSimilarId: '', addSimilarName: '' })
      if (r) this.partyChoose(box, r, '')
      this.partyFocusInput(box)
    },
    // Focus a quick-add field once the form is in the DOM.
    partyFocusField(box, name) {
      const go = () => {
        const field = box.PartyAdd && box.PartyAdd['AppField_' + name]
        const input = field && field.AppInput
        if (input && input.node && typeof input.node.focus === 'function') input.node.focus()
      }
      const st = box.state
      if (st && typeof st.$settled === 'function') st.$settled().then(go)
      else go()
    },
    // Back to the picker's field WITHOUT opening the list (its focus opens it).
    partyFocusInput(box) {
      const input = box.AppInputGroup && box.AppInputGroup.AppInputBare
      if (!input || !input.node || typeof input.node.focus !== 'function') return
      box.scope.partyQuiet = true
      try {
        input.node.focus()
      } finally {
        box.scope.partyQuiet = false
      }
    }
  },

  // The chosen party's name: on mount, and whenever `current` changes.
  stateDeps: [(el) => el.scope.partyCurrent(el)],
  onStateUpdate: (el) => el.scope.partyResolve(el),
  onRender: (el) => el.scope.partyResolve(el),
  onBeforeRemove: (el) => {
    el.scope.partyGone = true
    clearTimeout(el.scope.partyTimer)
  },

  AppInputGroup: {
    AppInputBare: {
      onFocus: (e, el) => {
        if (el.scope.partyQuiet) return
        el.scope.open(el.parent.parent)
      },
      onInput: (e, el, s) => {
        s.update({ query: e.target.value, open: true, active: 0, note: '', noteName: '' })
        el.scope.partySearchSoon(el.parent.parent)
      }
    }
  },

  ComboList: {
    children: (el, s) => {
      if (!s.open) return []
      const shown = el.scope.matches(el.parent, s).shown
      return shown.map((o, i) => ({
        key: 'o-' + o.value,
        value: o.value,
        label: String(o.label),
        hint: o.hint ? String(o.hint) : '',
        isAdd: !!o.isAdd,
        index: i,
        optionCount: shown.length
      }))
    },
    childProps: {
      ':hover': { background: 'var(--color-hover)' },
      ':active': { background: 'var(--color-selected)' },
      '&[data-active]': { background: 'var(--color-selected)' },
      // the "New client" row leads with a plus
      Icon_add: {
        name: 'plus',
        order: '-1',
        flexShrink: '0',
        ariaHidden: 'true',
        hide: (el, s) => !s.isAdd
      }
    }
  },

  ComboStatus: {
    marginTop: (el, s) => (el.scope.partyStatus(el.parent, s) ? 'X' : '0'),
    text: (el, s) => el.scope.partyStatus(el.parent, s)
  },

  // The quick add: in flow under the field (a dialog body is a scroll
  // container; an in-flow form is never clipped), on the neutral plate.
  PartyAdd: {
    role: 'group',
    ariaLabelledby: (el) => el.scope.id(el.parent, 'add-title'),
    hide: (el, s) => !s.adding,
    flow: 'y',
    gap: 'Z1',
    marginTop: 'Z',
    padding: 'A',
    round: 'radiusCard',
    background: 'widgetPlate',
    minWidth: '0',
    onKeydown: (e, el) => el.parent.scope.partyAddKey(el.parent, e),

    // the form's title: the kit caption, by its bare key
    AppFieldLabel: {
      id: (el) => el.scope.id(el.parent.parent, 'add-title'),
      text: (el) => el.call('polyglot', el.parent.parent.addLabel || 'common.party.addNew')
    },

    // Company · Person — only when the picker takes either kind.
    AppFieldSegmented: {
      hide: (el) => el.scope.partyKindOf(el.parent.parent) !== 'any',
      label: (el) => el.call('polyglot', 'common.party.kind'),
      options: [
        { value: 'company', label: 'common.party.kindCompany' },
        { value: 'person', label: 'common.party.kindPerson' }
      ],
      stateKey: 'addKind'
    },

    AppField_name: {
      label: (el) => el.call('polyglot', 'common.party.name'),
      reserveError: true,
      AppInput: {
        maxlength: '160',
        autocomplete: 'off',
        ariaRequired: 'true',
        ariaInvalid: (el, s) => (s.addNameError ? 'true' : null),
        class: { hasError: (el, s) => !!s.addNameError },
        value: (el, s) => s.addName || '',
        onInput: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddSet(box, 'addName', e.target.value)
        }
      },
      FieldError: {
        text: (el, s) => (s.addNameError ? el.call('polyglot', 'common.party.nameRequired') : ''),
        visibility: (el, s) => (s.addNameError ? 'visible' : 'hidden')
      }
    },

    AppField_legal: {
      hide: (el, s) => s.addKind !== 'company',
      label: (el) => el.call('polyglot', 'common.party.legalName'),
      AppInput: {
        maxlength: '200',
        autocomplete: 'off',
        value: (el, s) => s.addLegalName || '',
        onInput: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddSet(box, 'addLegalName', e.target.value)
        }
      }
    },

    // A company's tax ID; a person's personal ID — the same `taxId` field.
    AppField_tax: {
      label: (el, s) =>
        el.call(
          'polyglot',
          s.addKind === 'person' ? 'common.party.personalId' : 'common.party.taxId'
        ),
      AppInput: {
        maxlength: '64',
        autocomplete: 'off',
        spellcheck: 'false',
        value: (el, s) => s.addTaxId || '',
        onInput: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddSet(box, 'addTaxId', e.target.value)
        }
      }
    },

    AppField_email: {
      label: (el) => el.call('polyglot', 'common.party.email'),
      AppInput: {
        type: 'email',
        inputmode: 'email',
        maxlength: '200',
        autocomplete: 'off',
        spellcheck: 'false',
        value: (el, s) => s.addEmail || '',
        onInput: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddSet(box, 'addEmail', e.target.value)
        }
      }
    },

    AppField_phone: {
      label: (el) => el.call('polyglot', 'common.party.phone'),
      AppInput: {
        type: 'tel',
        inputmode: 'tel',
        maxlength: '40',
        autocomplete: 'off',
        value: (el, s) => s.addPhone || '',
        onInput: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddSet(box, 'addPhone', e.target.value)
        }
      }
    },

    // A similar name: warned once — use that client, or add anyway.
    PartyAddSimilar: {
      hide: (el, s) => !s.addSimilarId,
      flow: 'x',
      align: 'center flex-start',
      flexWrap: 'wrap',
      gap: 'Z',
      Icon: {
        name: 'warning',
        color: 'warningInk',
        flexShrink: '0',
        ariaHidden: 'true'
      },
      PartyAddSimilarText: {
        tag: 'span',
        role: 'alert',
        flex: '1 1 auto',
        minWidth: '0',
        fontSize: 'Z1',
        color: 'title',
        overflowWrap: 'anywhere',
        text: (el, s) => {
          const box = el.parent.parent.parent
          return s.addSimilarId
            ? box.scope.partyCopy(box, 'common.party.similarName', s.addSimilarName)
            : ''
        }
      },
      AppCapsuleBtnToolbar: {
        compact: true,
        text: (el, s) => {
          const box = el.parent.parent.parent
          return s.addSimilarId
            ? box.scope.partyCopy(box, 'common.party.useExisting', s.addSimilarName)
            : ''
        },
        onClick: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyUseSimilar(box)
        }
      },
      AppCapsuleBtnGhost: {
        compact: true,
        disabled: (el, s) => !!s.addBusy,
        text: (el) => el.call('polyglot', 'common.party.addAnyway'),
        onClick: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddRun(box, true)
        }
      }
    },

    PartyAddError: {
      tag: 'span',
      role: 'alert',
      fontSize: 'Z1',
      fontWeight: '500',
      color: 'dangerInk',
      overflowWrap: 'anywhere',
      hide: (el, s) => !s.addError,
      text: (el, s) => (s.addError ? el.call('polyglot', s.addError) : '')
    },

    PartyAddFoot: {
      flow: 'x',
      align: 'center flex-end',
      flexWrap: 'wrap',
      gap: 'Z',
      AppCapsuleBtnGhost: {
        compact: true,
        text: (el) => el.call('polyglot', 'common.action.cancel'),
        onClick: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddClose(box)
        }
      },
      AppCapsuleBtnPrimary: {
        compact: true,
        disabled: (el, s) => !!s.addBusy,
        text: (el) => el.call('polyglot', 'common.action.add'),
        onClick: (e, el) => {
          const box = el.parent.parent.parent
          box.scope.partyAddRun(box, false)
        }
      }
    }
  }
}
